import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Save, 
  RotateCcw, 
  AlertCircle,
  Loader2,
  Layout,
  History,
  ExternalLink,
  Palette,
  X,
  Trash2,
  Eye,
  EyeOff,
  Monitor,
  Smartphone,
  Tablet,
  Star,
  Pencil,
  Settings,
  Move,
  ChevronUp,
  ChevronDown,
  Plus,
  Image as ImageIcon,
  Type,
  GripVertical,
  Undo2,
  Redo2,
  MousePointer,
  Layers,
  Menu,
  Video,
  Upload,
  Play,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import VisualEditorCanvas from '../../components/VisualEditorCanvas';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Color presets for the brand
const BRAND_COLORS = {
  basePrincipal: '#1c262d',
  baseDark: '#0d1218',
  accentPrimary: '#93a5af',
  accentSecondary: '#2e8036',
};

const COLOR_PRESETS = [
  { name: 'Verde', value: '#2e8036' },
  { name: 'Gris Azul', value: '#93a5af' },
  { name: 'Base', value: '#1c262d' },
  { name: 'Oscuro', value: '#0d1218' },
  { name: 'Azul', value: '#3B82F6' },
  { name: 'Rojo', value: '#EF4444' },
  { name: 'Amarillo', value: '#EAB308' },
  { name: 'Blanco', value: '#FFFFFF' },
];

// Floating color picker component
const FloatingColorPicker = ({ value, onChange, onClose, position }) => {
  const [customColor, setCustomColor] = useState(value || '#2e8036');
  
  return (
    <div 
      className="fixed z-[9999] bg-[#1c262d] border border-white/20 rounded-xl shadow-2xl p-4 w-64"
      style={{ 
        top: position?.y || 100, 
        left: position?.x || 100,
        transform: 'translate(-50%, 10px)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-white text-sm font-medium">Selector de Color</span>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Color presets grid */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {COLOR_PRESETS.map((preset) => (
          <button
            key={preset.value}
            onClick={() => {
              onChange(preset.value);
              onClose();
            }}
            className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
              value === preset.value ? 'border-white ring-2 ring-white/30' : 'border-white/20'
            }`}
            style={{ backgroundColor: preset.value }}
            title={preset.name}
          />
        ))}
      </div>
      
      {/* Custom color input */}
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
          className="w-10 h-10 rounded-lg cursor-pointer border-0"
        />
        <Input
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
          className="flex-1 bg-[#0d1218] border-white/10 text-white text-sm font-mono"
          placeholder="#000000"
        />
        <Button
          size="sm"
          onClick={() => {
            onChange(customColor);
            onClose();
          }}
          className="bg-[#2e8036] hover:bg-[#247029]"
        >
          OK
        </Button>
      </div>
    </div>
  );
};

// Section visibility toggle component
const SectionVisibilityPanel = ({ sections, onToggle, onReorder }) => {
  return (
    <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
        <Layers className="w-4 h-4 text-[#2e8036]" />
        Secciones
      </h3>
      <div className="space-y-2">
        {sections.map((section, index) => (
          <div 
            key={section.id}
            className="flex items-center gap-2 p-2 bg-[#0d1218] rounded-lg group"
          >
            <GripVertical className="w-4 h-4 text-slate-500 cursor-move" />
            <button
              onClick={() => onToggle(section.id)}
              className={`p-1 rounded ${section.visible ? 'text-[#2e8036]' : 'text-slate-500'}`}
            >
              {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <span className={`flex-1 text-sm ${section.visible ? 'text-white' : 'text-slate-500'}`}>
              {section.name}
            </span>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                onClick={() => onReorder(index, 'up')}
                disabled={index === 0}
                className="p-1 text-slate-400 hover:text-white disabled:opacity-30"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button 
                onClick={() => onReorder(index, 'down')}
                disabled={index === sections.length - 1}
                className="p-1 text-slate-400 hover:text-white disabled:opacity-30"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Global styles panel
const GlobalStylesPanel = ({ styles, onChange }) => {
  const [activeColor, setActiveColor] = useState(null);
  const [colorPickerPos, setColorPickerPos] = useState({ x: 0, y: 0 });

  const handleColorClick = (e, colorKey) => {
    const rect = e.target.getBoundingClientRect();
    setColorPickerPos({ x: rect.left + rect.width / 2, y: rect.bottom });
    setActiveColor(colorKey);
  };

  const styleItems = [
    { key: 'accentColor', label: 'Color de Acento', icon: '🎨' },
    { key: 'primaryColor', label: 'Color Principal', icon: '🔵' },
    { key: 'secondaryColor', label: 'Color Secundario', icon: '🟢' },
    { key: 'heroBackground', label: 'Fondo Hero', icon: '🖼️' },
    { key: 'sectionBackground', label: 'Fondo Secciones', icon: '📦' },
  ];

  return (
    <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
        <Palette className="w-4 h-4 text-[#2e8036]" />
        Estilos Globales
      </h3>
      <div className="space-y-3">
        {styleItems.map((item) => (
          <div key={item.key} className="flex items-center gap-3">
            <button
              onClick={(e) => handleColorClick(e, item.key)}
              className="w-8 h-8 rounded-lg border-2 border-white/20 hover:border-white/40 transition-all hover:scale-110"
              style={{ backgroundColor: styles?.[item.key] || '#2e8036' }}
            />
            <span className="text-sm text-slate-300 flex-1">{item.label}</span>
          </div>
        ))}
      </div>
      
      {activeColor && (
        <FloatingColorPicker
          value={styles?.[activeColor]}
          onChange={(color) => {
            onChange(activeColor, color);
            setActiveColor(null);
          }}
          onClose={() => setActiveColor(null)}
          position={colorPickerPos}
        />
      )}
    </div>
  );
};

export default function VisualPageBuilder() {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [originalContent, setOriginalContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [version, setVersion] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [editMode, setEditMode] = useState('visual'); // 'visual' | 'preview'
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [videoUploadLoading, setVideoUploadLoading] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    embedUrl: '',
    videoUrl: '',
    videoFile: null,
    thumbnail: '',
    thumbnailFile: null
  });
  
  // FAQ management state
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: ''
  });
  
  // Section visibility state
  const [sectionVisibility, setSectionVisibility] = useState({
    hero: true,
    howItWorks: true,
    benefits: true,
    testimonials: true,
    videos: true,
    faq: true,
    cta: true,
    contact: true,
  });

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (content && originalContent) {
      setHasChanges(JSON.stringify(content) !== JSON.stringify(originalContent));
    }
  }, [content, originalContent]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/content/landing`);
      const contentData = response.data.content || {};
      if (!contentData.styles) {
        contentData.styles = {
          primaryColor: '#93a5af',
          secondaryColor: '#2e8036',
          heroBackground: '#0d1218',
          sectionBackground: '#1c262d',
          textColor: '#FFFFFF',
          accentColor: '#2e8036'
        };
      }
      if (!contentData.faq) {
        contentData.faq = {
          title: 'Perguntas Frequentes',
          subtitle: 'Encontre respostas às questões mais comuns.',
          items: []
        };
      }
      setContent(contentData);
      setOriginalContent(JSON.parse(JSON.stringify(contentData)));
      setVersion(response.data.version || 0);
    } catch (error) {
      // Error silenced: 'Error loading content:', error);
      toast.error('Error al cargar contenido');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/content/landing/history`);
      setHistory(response.data.history || []);
      setShowHistory(true);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const pushToUndoStack = useCallback(() => {
    if (content) {
      setUndoStack(prev => [...prev.slice(-19), JSON.stringify(content)]);
      setRedoStack([]);
    }
  }, [content]);

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previous = undoStack[undoStack.length - 1];
      setRedoStack(prev => [...prev, JSON.stringify(content)]);
      setUndoStack(prev => prev.slice(0, -1));
      setContent(JSON.parse(previous));
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const next = redoStack[redoStack.length - 1];
      setUndoStack(prev => [...prev, JSON.stringify(content)]);
      setRedoStack(prev => prev.slice(0, -1));
      setContent(JSON.parse(next));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/admin/content/landing`, {
        page_id: 'landing',
        content: content
      });
      setOriginalContent(JSON.parse(JSON.stringify(content)));
      setVersion(response.data.version);
      toast.success('¡Contenido guardado con éxito!');
    } catch (error) {
      toast.error('Error al guardar contenido');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setContent(JSON.parse(JSON.stringify(originalContent)));
    toast.info('Cambios descartados');
  };

  const handleRollback = async (targetVersion) => {
    if (!window.confirm(`¿Restaurar a la versión ${targetVersion}?`)) return;
    
    try {
      await axios.post(`${API_URL}/admin/content/landing/rollback/${targetVersion}`);
      await fetchContent();
      setShowHistory(false);
      toast.success(`Restaurado a versión ${targetVersion}`);
    } catch (error) {
      toast.error('Error al restaurar versión');
    }
  };

  const updateContent = useCallback((path, value) => {
    pushToUndoStack();
    setContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = newContent;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return newContent;
    });
  }, [pushToUndoStack]);

  const updateStyle = (key, value) => {
    updateContent(`styles.${key}`, value);
  };

  const toggleSectionVisibility = (sectionId) => {
    setSectionVisibility(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
    // Also update content to persist
    updateContent(`visibility.${sectionId}`, !sectionVisibility[sectionId]);
  };

  const sectionsList = [
    { id: 'hero', name: 'Hero Principal', visible: sectionVisibility.hero },
    { id: 'howItWorks', name: 'Cómo Funciona', visible: sectionVisibility.howItWorks },
    { id: 'benefits', name: 'Beneficios', visible: sectionVisibility.benefits },
    { id: 'testimonials', name: 'Testimonios', visible: sectionVisibility.testimonials },
    { id: 'videos', name: 'Videos', visible: sectionVisibility.videos },
    { id: 'faq', name: 'Preguntas Frecuentes', visible: sectionVisibility.faq },
    { id: 'cta', name: 'CTA Banner', visible: sectionVisibility.cta },
    { id: 'contact', name: 'Contacto', visible: sectionVisibility.contact },
  ];

  const handleReorderSection = (index, direction) => {
    // Placeholder for drag-and-drop reordering
    toast.info('Función de reordenamiento próximamente');
  };

  // Get preview width based on device
  const getPreviewWidth = () => {
    switch (previewDevice) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#2e8036] animate-spin" />
      </div>
    );
  }

  return (
    <div className="-m-6 min-h-[calc(100vh-64px)] flex flex-col bg-[#0d1218]" data-testid="visual-page-builder">
      {/* Top Toolbar */}
      <div className="bg-[#0d1218] border-b border-white/10 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <Layout className="w-5 h-5 text-[#2e8036]" />
            Editor Visual
          </h1>
          
          {/* Mode Toggle */}
          <div className="flex items-center bg-[#1c262d] rounded-lg p-1">
            <button
              onClick={() => setEditMode('visual')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                editMode === 'visual' 
                  ? 'bg-[#2e8036] text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('admin.common.edit')}
            </button>
            <button
              onClick={() => setEditMode('preview')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                editMode === 'preview' 
                  ? 'bg-[#2e8036] text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              {t('admin.common.preview')}
            </button>
          </div>

          {/* Undo/Redo */}
          <div className="flex items-center gap-1 border-l border-white/10 pl-4">
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
              title={t('admin.common.undo')}
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className="p-2 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
              title={t('admin.common.redo')}
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Device Switcher */}
          <div className="flex items-center gap-1 bg-[#1c262d] rounded-lg p-1">
            <button
              onClick={() => setPreviewDevice('desktop')}
              className={`p-1.5 rounded transition-colors ${
                previewDevice === 'desktop' ? 'bg-[#2e8036]/20 text-[#2e8036]' : 'text-slate-400 hover:text-white'
              }`}
              title="Desktop"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewDevice('tablet')}
              className={`p-1.5 rounded transition-colors ${
                previewDevice === 'tablet' ? 'bg-[#2e8036]/20 text-[#2e8036]' : 'text-slate-400 hover:text-white'
              }`}
              title="Tablet"
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewDevice('mobile')}
              className={`p-1.5 rounded transition-colors ${
                previewDevice === 'mobile' ? 'bg-[#2e8036]/20 text-[#2e8036]' : 'text-slate-400 hover:text-white'
              }`}
              title="Mobile"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          {hasChanges && (
            <span className="text-amber-400 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {t('admin.pageBuilder.unsavedChanges')}
            </span>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={fetchHistory}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <History className="w-4 h-4 mr-1" />
            {t('admin.pageBuilder.viewHistory')}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('/', '_blank')}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            {t('admin.pageBuilder.viewLive')}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleDiscard}
            disabled={!hasChanges}
            className="border-white/10 text-slate-300 hover:bg-white/5"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            {t('admin.common.reset')}
          </Button>

          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-[#2e8036] hover:bg-[#247029]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-1" />
            )}
            {t('admin.common.save')}
          </Button>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        {showLeftPanel && (
          <div className="w-72 bg-[#0d1218] border-r border-white/10 overflow-y-auto flex-shrink-0 p-4 space-y-4">
            <GlobalStylesPanel 
              styles={content?.styles} 
              onChange={updateStyle}
            />

            {/* Header Editor Panel */}
            <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Menu className="w-4 h-4 text-[#2e8036]" />
                Menu de Navegação
              </h3>
              <div className="space-y-3">
                {/* Menu Items */}
                <div className="space-y-2">
                  <Label className="text-sm text-slate-400">Itens do Menu</Label>
                  {content?.header?.menuItems?.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={item.label}
                        onChange={(e) => {
                          const newItems = [...(content?.header?.menuItems || [])];
                          newItems[index] = { ...newItems[index], label: e.target.value };
                          updateContent('header.menuItems', newItems);
                        }}
                        className="flex-1 bg-[#0d1218] border-white/10 text-white text-sm"
                        placeholder="Nome do item"
                      />
                      <Input
                        value={item.href}
                        onChange={(e) => {
                          const newItems = [...(content?.header?.menuItems || [])];
                          newItems[index] = { ...newItems[index], href: e.target.value };
                          updateContent('header.menuItems', newItems);
                        }}
                        className="w-32 bg-[#0d1218] border-white/10 text-white text-sm"
                        placeholder="#secao"
                      />
                      <button
                        onClick={() => {
                          const newItems = content?.header?.menuItems?.filter((_, i) => i !== index) || [];
                          updateContent('header.menuItems', newItems);
                        }}
                        className="p-1.5 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newItems = [...(content?.header?.menuItems || []), { label: 'Novo Item', href: '#nova-secao' }];
                      updateContent('header.menuItems', newItems);
                    }}
                    className="w-full border-dashed border-white/20 text-slate-400 hover:text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Item
                  </Button>
                </div>
                
                {/* Login/Register Texts */}
                <div className="pt-3 border-t border-white/10 space-y-2">
                  <div>
                    <Label className="text-xs text-slate-400">Texto do Botão Login</Label>
                    <Input
                      value={content?.header?.loginText || 'Iniciar Sessão'}
                      onChange={(e) => updateContent('header.loginText', e.target.value)}
                      className="bg-[#0d1218] border-white/10 text-white text-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Texto do Botão Registo</Label>
                    <Input
                      value={content?.header?.registerText || 'Criar Conta'}
                      onChange={(e) => updateContent('header.registerText', e.target.value)}
                      className="bg-[#0d1218] border-white/10 text-white text-sm mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <SectionVisibilityPanel 
              sections={sectionsList}
              onToggle={toggleSectionVisibility}
              onReorder={handleReorderSection}
            />

            {/* Quick Tips */}
            <div className="bg-[#2e8036]/10 border border-[#2e8036]/20 rounded-xl p-4">
              <h3 className="text-[#2e8036] font-medium mb-2 flex items-center gap-2">
                <MousePointer className="w-4 h-4" />
                Consejos
              </h3>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Haz clic en cualquier texto para editarlo</li>
                <li>• Pasa el mouse sobre elementos para ver opciones</li>
                <li>• Usa el icono 🎨 para cambiar colores</li>
                <li>• Ctrl+Z para deshacer</li>
              </ul>
            </div>

            {/* Video Management Panel */}
            <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Video className="w-4 h-4 text-purple-400" />
                Gestão de Vídeos
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Carregue vídeos do seu computador ou adicione links do YouTube
              </p>
              
              {/* Video List */}
              <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                {content?.videos?.items?.map((video, index) => (
                  <div 
                    key={video.id || index}
                    className="flex items-center gap-2 p-2 bg-[#0d1218] rounded-lg group"
                  >
                    <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Play className="w-4 h-4 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{video.title}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {video.videoUrl ? 'Vídeo local' : video.embedUrl ? 'YouTube/Vimeo' : 'Sem vídeo'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const newItems = content?.videos?.items?.filter((_, i) => i !== index) || [];
                        updateContent('videos.items', newItems);
                        // If it's a local video, delete from server
                        if (video.videoUrl && video.videoUrl.includes('/api/uploads/videos/')) {
                          const filename = video.videoUrl.split('/').pop();
                          axios.delete(`${API_URL}/admin/content/delete-video/${filename}`).catch(() => {});
                        }
                      }}
                      className="p-1 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add Video Button */}
              <button
                onClick={() => setShowVideoDialog(true)}
                className="w-full p-2 border-2 border-dashed border-purple-500/30 rounded-lg text-purple-400 text-sm hover:border-purple-500/50 hover:bg-purple-500/5 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Adicionar Vídeo
              </button>
            </div>

            {/* FAQ Management Panel */}
            <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-cyan-400" />
                Gestão de FAQ
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Adicione e edite perguntas frequentes que aparecerão na landing page
              </p>
              
              {/* FAQ List */}
              <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                {content?.faq?.items?.map((faq, index) => (
                  <div 
                    key={faq.id || index}
                    className="flex items-center gap-2 p-2 bg-[#0d1218] rounded-lg group"
                  >
                    <div className="w-8 h-8 rounded bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{faq.question}</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingFaq(index);
                        setNewFaq({ question: faq.question, answer: faq.answer });
                        setShowFaqDialog(true);
                      }}
                      className="p-1 text-cyan-400 hover:text-cyan-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`edit-faq-${index}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const newItems = content?.faq?.items?.filter((_, i) => i !== index) || [];
                        updateContent('faq.items', newItems);
                        toast.success('Pergunta eliminada');
                      }}
                      className="p-1 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-faq-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {(!content?.faq?.items || content?.faq?.items?.length === 0) && (
                  <p className="text-xs text-slate-500 text-center py-2">Nenhuma pergunta adicionada</p>
                )}
              </div>

              {/* Add FAQ Button */}
              <button
                onClick={() => {
                  setEditingFaq(null);
                  setNewFaq({ question: '', answer: '' });
                  setShowFaqDialog(true);
                }}
                className="w-full p-2 border-2 border-dashed border-cyan-500/30 rounded-lg text-cyan-400 text-sm hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-colors flex items-center justify-center gap-2"
                data-testid="add-faq-btn"
              >
                <Plus className="w-4 h-4" />
                Adicionar Pergunta
              </button>
            </div>

            {/* Testimonials Images Management */}
            <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                Imagens de Testemunhos
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Carregue capturas de ecrã dos testemunhos dos seus clientes
              </p>
              
              {/* Images Grid */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {content?.testimonials?.images?.map((img, index) => (
                  <div 
                    key={img.id || index}
                    className="relative group aspect-video bg-[#0d1218] rounded-lg overflow-hidden"
                  >
                    <img 
                      src={img.url} 
                      alt={img.alt || `Testemunho ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => {
                        const newImages = content?.testimonials?.images?.filter((_, i) => i !== index) || [];
                        updateContent('testimonials.images', newImages);
                        toast.success('Imagem eliminada');
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-500/80 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Upload Button */}
              <div className="border-2 border-dashed border-yellow-500/30 rounded-lg p-3 hover:border-yellow-500/50 transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length === 0) return;
                    
                    const newImages = [];
                    for (const file of files) {
                      const reader = new FileReader();
                      await new Promise((resolve) => {
                        reader.onload = (event) => {
                          newImages.push({
                            id: `testimonial_img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            url: event.target.result,
                            alt: file.name.replace(/\.[^/.]+$/, '')
                          });
                          resolve();
                        };
                        reader.readAsDataURL(file);
                      });
                    }
                    
                    const currentImages = content?.testimonials?.images || [];
                    updateContent('testimonials.images', [...currentImages, ...newImages]);
                    toast.success(`${newImages.length} imagem(ns) adicionada(s)`);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="testimonial-images-upload"
                />
                <label 
                  htmlFor="testimonial-images-upload"
                  className="flex flex-col items-center gap-2 cursor-pointer"
                >
                  <Upload className="w-6 h-6 text-yellow-400" />
                  <span className="text-sm text-slate-300">Carregar imagens</span>
                  <span className="text-xs text-slate-500">PNG, JPG, WebP</span>
                </label>
              </div>
            </div>

            {/* Legal Pages Editor Links */}
            <div className="bg-[#1c262d] border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-400" />
                Páginas Legais
              </h3>
              <div className="space-y-2">
                <a
                  href="/admin/legal-editor/privacy"
                  className="flex items-center gap-2 p-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-lg text-sm text-slate-300 transition-colors"
                >
                  <span className="text-blue-400">🔒</span>
                  Política de Privacidade
                </a>
                <a
                  href="/admin/legal-editor/terms"
                  className="flex items-center gap-2 p-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-lg text-sm text-slate-300 transition-colors"
                >
                  <span className="text-emerald-400">📄</span>
                  Termos e Condições
                </a>
                <a
                  href="/admin/legal-editor/cookies"
                  className="flex items-center gap-2 p-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg text-sm text-slate-300 transition-colors"
                >
                  <span className="text-amber-400">🍪</span>
                  Política de Cookies
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Canvas Area */}
        <div className="flex-1 bg-[#252F38] overflow-hidden flex flex-col relative">
          {/* Toggle Left Panel Button */}
          <button
            onClick={() => setShowLeftPanel(!showLeftPanel)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[#1c262d] border border-white/10 rounded-r-lg p-2 text-slate-400 hover:text-white transition-colors"
          >
            {showLeftPanel ? <ChevronUp className="w-4 h-4 rotate-[-90deg]" /> : <ChevronDown className="w-4 h-4 rotate-[-90deg]" />}
          </button>

          {/* Canvas Container */}
          <div className="flex-1 overflow-auto flex justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300"
              style={{ 
                width: getPreviewWidth(),
                maxWidth: '100%',
                minHeight: '100%'
              }}
            >
              <VisualEditorCanvas 
                content={content}
                editMode={editMode}
                onUpdateContent={updateContent}
                sectionVisibility={sectionVisibility}
                previewDevice={previewDevice}
              />
            </div>
          </div>
        </div>
      </div>

      {/* History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-[#2e8036]" />
              Historial de Versiones
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No hay historial disponible</p>
            ) : (
              history.map((item) => (
                <div 
                  key={item.version}
                  className="bg-[#0d1218] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-white font-medium">Versión {item.version}</p>
                    <p className="text-slate-400 text-sm">
                      {new Date(item.updated_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRollback(item.version)}
                    className="border-white/10 text-slate-300 hover:bg-white/5"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restaurar
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Upload Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit'] flex items-center gap-2">
              <Video className="w-5 h-5 text-purple-400" />
              Adicionar Vídeo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Title */}
            <div>
              <Label className="text-slate-300">Título do Vídeo</Label>
              <Input
                value={newVideo.title}
                onChange={(e) => setNewVideo(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Apresentação da SGFI Capital"
                className="mt-1 bg-[#0d1218] border-white/10 text-white"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-slate-300">Descrição</Label>
              <Textarea
                value={newVideo.description}
                onChange={(e) => setNewVideo(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Breve descrição do vídeo..."
                rows={2}
                className="mt-1 bg-[#0d1218] border-white/10 text-white resize-none"
              />
            </div>

            {/* Video Source Tabs */}
            <div className="space-y-3">
              <Label className="text-slate-300">Origem do Vídeo</Label>
              
              {/* Upload File */}
              <div className="border-2 border-dashed border-purple-500/30 rounded-xl p-4 hover:border-purple-500/50 transition-colors">
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/ogg,video/quicktime"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setNewVideo(prev => ({ ...prev, videoFile: file, embedUrl: '' }));
                    }
                  }}
                  className="hidden"
                  id="video-upload"
                />
                <label 
                  htmlFor="video-upload"
                  className="flex flex-col items-center gap-2 cursor-pointer"
                >
                  <Upload className="w-8 h-8 text-purple-400" />
                  <span className="text-sm text-slate-300">
                    {newVideo.videoFile ? newVideo.videoFile.name : 'Clique para carregar um vídeo'}
                  </span>
                  <span className="text-xs text-slate-500">MP4, WebM, OGG • Máx. 100MB</span>
                </label>
              </div>

              {/* Or separator */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-white/10"></div>
                <span className="text-slate-500 text-xs">OU</span>
                <div className="flex-1 border-t border-white/10"></div>
              </div>

              {/* CDN/External URL (Bunny, etc) */}
              <div>
                <Label className="text-slate-400 text-xs">Link CDN (Bunny.net, etc) - Recomendado</Label>
                <Input
                  value={newVideo.videoUrl || ''}
                  onChange={(e) => setNewVideo(prev => ({ ...prev, videoUrl: e.target.value, videoFile: null, embedUrl: '' }))}
                  placeholder="https://vz-xxx.b-cdn.net/.../playlist.m3u8"
                  className="mt-1 bg-[#0d1218] border-white/10 text-white"
                />
                <p className="text-xs text-green-400 mt-1">⚡ Streaming rápido com HLS</p>
              </div>

              {/* Or separator */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-white/10"></div>
                <span className="text-slate-500 text-xs">OU</span>
                <div className="flex-1 border-t border-white/10"></div>
              </div>

              {/* YouTube/Vimeo URL */}
              <div>
                <Label className="text-slate-400 text-xs">Link do YouTube ou Vimeo</Label>
                <Input
                  value={newVideo.embedUrl}
                  onChange={(e) => setNewVideo(prev => ({ ...prev, embedUrl: e.target.value, videoFile: null, videoUrl: '' }))}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="mt-1 bg-[#0d1218] border-white/10 text-white"
                />
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div>
              <Label className="text-slate-300">Imagem de Pré-visualização (opcional)</Label>
              <p className="text-xs text-slate-500 mb-2">Carregue uma imagem para mostrar antes do vídeo carregar</p>
              <div className="border-2 border-dashed border-cyan-500/30 rounded-xl p-3 hover:border-cyan-500/50 transition-colors">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setNewVideo(prev => ({ ...prev, thumbnailFile: file, thumbnail: event.target.result }));
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="hidden"
                  id="thumbnail-upload"
                />
                <label 
                  htmlFor="thumbnail-upload"
                  className="flex items-center gap-3 cursor-pointer"
                >
                  {newVideo.thumbnail ? (
                    <img src={newVideo.thumbnail} alt="Thumbnail" className="w-16 h-10 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-10 bg-cyan-500/20 rounded flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-cyan-400" />
                    </div>
                  )}
                  <span className="text-sm text-slate-300">
                    {newVideo.thumbnailFile ? newVideo.thumbnailFile.name : 'Clique para carregar imagem'}
                  </span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowVideoDialog(false);
                  setNewVideo({ title: '', description: '', embedUrl: '', videoUrl: '', videoFile: null, thumbnail: '', thumbnailFile: null });
                }}
                variant="ghost"
                className="flex-1 text-slate-300 hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!newVideo.title) {
                    toast.error('Por favor, insira um título');
                    return;
                  }
                  if (!newVideo.videoFile && !newVideo.embedUrl && !newVideo.videoUrl) {
                    toast.error('Por favor, carregue um vídeo ou insira um link');
                    return;
                  }

                  setVideoUploadLoading(true);
                  try {
                    let videoUrl = newVideo.videoUrl || '';
                    
                    // If uploading a file
                    if (newVideo.videoFile) {
                      const formData = new FormData();
                      formData.append('file', newVideo.videoFile);
                      
                      const uploadResponse = await axios.post(
                        `${API_URL}/admin/content/upload-video`,
                        formData,
                        { headers: { 'Content-Type': 'multipart/form-data' } }
                      );
                      videoUrl = uploadResponse.data.url;
                    }

                    // Add to content
                    const newVideoItem = {
                      id: `video_${Date.now()}`,
                      title: newVideo.title,
                      description: newVideo.description,
                      videoUrl: videoUrl,
                      embedUrl: newVideo.embedUrl || '',
                      thumbnail: newVideo.thumbnail || ''
                    };

                    const currentItems = content?.videos?.items || [];
                    updateContent('videos.items', [...currentItems, newVideoItem]);
                    
                    toast.success('Vídeo adicionado com sucesso');
                    setShowVideoDialog(false);
                    setNewVideo({ title: '', description: '', embedUrl: '', videoUrl: '', videoFile: null, thumbnail: '', thumbnailFile: null });
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Erro ao carregar vídeo');
                  } finally {
                    setVideoUploadLoading(false);
                  }
                }}
                disabled={videoUploadLoading || (!newVideo.videoFile && !newVideo.embedUrl && !newVideo.videoUrl)}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {videoUploadLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    A carregar...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={showFaqDialog} onOpenChange={setShowFaqDialog}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit'] flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-cyan-400" />
              {editingFaq !== null ? 'Editar Pergunta' : 'Adicionar Pergunta'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Question */}
            <div>
              <Label className="text-slate-300">Pergunta</Label>
              <Input
                value={newFaq.question}
                onChange={(e) => setNewFaq(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Ex: Como posso abrir uma conta?"
                className="mt-1 bg-[#0d1218] border-white/10 text-white"
                data-testid="faq-question-input"
              />
            </div>

            {/* Answer */}
            <div>
              <Label className="text-slate-300">Resposta</Label>
              <Textarea
                value={newFaq.answer}
                onChange={(e) => setNewFaq(prev => ({ ...prev, answer: e.target.value }))}
                placeholder="Digite a resposta detalhada..."
                rows={4}
                className="mt-1 bg-[#0d1218] border-white/10 text-white resize-none"
                data-testid="faq-answer-input"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowFaqDialog(false);
                  setNewFaq({ question: '', answer: '' });
                  setEditingFaq(null);
                }}
                variant="ghost"
                className="flex-1 text-slate-300 hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (!newFaq.question.trim()) {
                    toast.error('Por favor, insira a pergunta');
                    return;
                  }
                  if (!newFaq.answer.trim()) {
                    toast.error('Por favor, insira a resposta');
                    return;
                  }

                  const currentItems = content?.faq?.items || [];
                  
                  if (editingFaq !== null) {
                    // Edit existing FAQ
                    const updatedItems = [...currentItems];
                    updatedItems[editingFaq] = {
                      ...updatedItems[editingFaq],
                      question: newFaq.question.trim(),
                      answer: newFaq.answer.trim()
                    };
                    updateContent('faq.items', updatedItems);
                    toast.success('Pergunta atualizada com sucesso');
                  } else {
                    // Add new FAQ
                    const newFaqItem = {
                      id: `faq_${Date.now()}`,
                      question: newFaq.question.trim(),
                      answer: newFaq.answer.trim()
                    };
                    updateContent('faq.items', [...currentItems, newFaqItem]);
                    toast.success('Pergunta adicionada com sucesso');
                  }
                  
                  setShowFaqDialog(false);
                  setNewFaq({ question: '', answer: '' });
                  setEditingFaq(null);
                }}
                disabled={!newFaq.question.trim() || !newFaq.answer.trim()}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="save-faq-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {editingFaq !== null ? 'Guardar' : 'Adicionar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
